import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { FaDownload } from 'react-icons/fa';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import API from 'core/Api';
import { useLanguageContext } from 'store/language';
import { formatDate } from 'utils/helpers/formatters';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import InnerWrapper from 'routes/client/reports/InnerWrapper';
import { useModal } from 'store/modal';
import { useSelectableTableContext } from 'store/selectable-table';
import { requestMultipleDownload, downloadMultipleFiles, extractErrorMessage } from 'utils/helpers/archives';
import ReportForm from './components/ReportForm';
import DocsTable from './components/DocsTable';

const DocumentReport: FC = () => {
  const [reportParams, setReportParams] = useState(null);
  const [showDownloadBtn, setShowDownloadBtn] = useState(false);
  const [expandRows, setExpandRows] = useState(false);
  const [isDownloadMode, setIsDownloadMode] = useState(false);
  const { t } = useTranslation();
  const methods = useForm();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal, hideModal } = useModal();
  const { selectedRowsOnAction, resetSelectable, setShowSelectColumn, showSelectColumn } = useSelectableTableContext();

  useEffect(() => {
    resetSelectable();
    setShowSelectColumn(false);
  }, []);

  const {
    isLoading: isLoadingDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<any, Error>([buildClientEntity('report-dropdowns', client)], (): Promise<any> => {
    return API.get('reports/documents/dropdown-options').then(res => {
      return res.data[language || 'bg'];
    });
  });

  const handleSubmitReport = (data: any) => {
    let createdAtFrom = data.created_at_from;
    let createdAtTo = data.created_at_to;

    // Check if any date is selected
    const shouldPopulateDates = !!createdAtFrom || !!createdAtTo;

    if (shouldPopulateDates) {
      createdAtFrom = data.created_at_from && formatDate(data.created_at_from, 'YYY/mm/dd');

      createdAtTo = data.created_at_to
        ? formatDate(data.created_at_to, 'YYY/mm/dd')
        : formatDate(new Date(), 'YYY/mm/dd');

      /**
       * In case any of the date fields (but not both) is empty on submit -
       * set value so that the time period is visible in filters
       */
      if (createdAtFrom) methods.setValue('created_at_from', new Date(createdAtFrom));
      if (createdAtTo) methods.setValue('created_at_to', new Date(createdAtTo));
    }

    const formattedData = {
      ...data,
      search: data.search || null,
      created_at_from: createdAtFrom,
      created_at_to: createdAtTo,
      user_id: data.user_id?.map((user: any) => user.value),
      creator_id: data.creator_id?.map((creator: any) => creator.value),
      group_id: data.group_id?.map((group: any) => group.value),
      category_id: data.category_id?.map((category: any) => category.value),
    };

    setReportParams(formattedData);
  };

  const confirmBulkDownloadFlow = async (rowIds: number[]) => {
    if (rowIds.length === 0) {
      showModal({
        type: 'error',
        content: t('documents.table.noDocsSelected'),
      });
      return;
    }

    try {
      showModal({
        type: 'loading',
        heading: t('documents.bulkDownload.preparing'),
        content: t('documents.bulkDownload.pleaseWait'),
      });

      const response = await requestMultipleDownload(rowIds, 'reports-documents-tab');

      if (response.type === 'immediate-download') {
        hideModal();
        showModal({
          type: 'success',
          content: t('documents.bulkDownload.immediateDownloadMessage'),
        });

        downloadMultipleFiles(response.links);

        resetSelectable();
        setShowSelectColumn(false);
        setIsDownloadMode(false);
      } else {
        hideModal();
        showModal({
          type: 'success',
          heading: t('documents.bulkDownload.archiveGenerating'),
          content: t('documents.bulkDownload.archiveMessage'),
        });

        resetSelectable();
        setShowSelectColumn(false);
        setIsDownloadMode(false);
      }
    } catch (err: any) {
      hideModal();
      const errorMessage = extractErrorMessage(err, t('documents.bulkDownload.error'));
      showModal({
        type: 'error',
        content: errorMessage,
      });
    }
  };

  return (
    <InnerWrapper
      isLoading={isLoadingDropdowns}
      error={errorDropdowns}
    >
      {dropdowns && (
        <ReportForm
          data={dropdowns}
          methods={methods}
          onSubmit={handleSubmitReport}
          reportParams={reportParams}
          showDownloadBtn={showDownloadBtn}
          hideDownloadBtn={() => setShowDownloadBtn(false)}
        />
      )}
      {reportParams && (
        <div className="mt-3">
          {showDownloadBtn && !showSelectColumn && (
            <div className="d-flex justify-content-center justify-content-sm-end">
              <TooltipWrapper
                id="tooltip-mass-download"
                overlay={t('documents.bulkDownload.tooltip')}
              >
                <Button
                  className="mb-3 mr-3"
                  btnType="secondary"
                  onClick={() => {
                    setShowSelectColumn(true);
                    setIsDownloadMode(true);
                  }}
                  preventDebounce
                >
                  <FaDownload style={{ marginRight: '0.5rem' }} />
                  {t('documents.table.massDownload')}
                </Button>
              </TooltipWrapper>
              <Button
                className="mb-3"
                onClick={() => setExpandRows(!expandRows)}
                preventDebounce
              >
                {expandRows ? t('reports.collapseAll') : t('reports.expandAll')}
              </Button>
            </div>
          )}
          {showDownloadBtn && showSelectColumn && isDownloadMode && (
            <div className="d-flex justify-content-center justify-content-sm-end mb-3">
              <Button
                className="mr-2"
                btnType="secondary"
                onClick={() => selectedRowsOnAction(confirmBulkDownloadFlow)}
                preventDebounce
              >
                <FaDownload
                  size={14}
                  className="mr-2"
                />
                {t('documents.table.download')}
              </Button>
              <Button
                btnType="light"
                outlined
                onClick={() => {
                  resetSelectable();
                  setShowSelectColumn(false);
                  setIsDownloadMode(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
          <DocsTable
            reportParams={reportParams}
            expandAllRows={expandRows}
            setExpandAllRows={setExpandRows}
            setShowDownloadBtn={() => setShowDownloadBtn(true)}
            isDownloadMode={isDownloadMode}
          />
        </div>
      )}
    </InnerWrapper>
  );
};

export default DocumentReport;
